import { Button, Card, Typography, FormValues, FormObject, useAppForm } from '@stern-ui/ui';
import { AccountOutline } from 'mdi-material-ui';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import z from 'zod';

import CreateListSvg from '@/assets/create_list.svg';
import {
	SummaryFilter,
	selectRowsTotal,
	isFiltersApplied,
	selectFilterValues,
	FilterValuesObj,
	selectSearchValue,
} from '@/components';
import { addNotification } from '@/components/Notifications/notificationsSlice';
import { createDynamicListFeatureFlagEnabled } from '@/globalConsts/featureFlags';
import { trackEvent } from '@/lib/analytics';

import { CreatePatientListRequest } from '../../types';

type CreateListFormProps = {
	submitFormAction: (values: CreatePatientListRequest) => void;
};

const schema = z.object({
	name: z.string().min(1, 'List name is required'),
	filters: z.custom<FormValues>(),
	search_value: z.union([z.string(), z.undefined()]),
	list_type: z.enum(['static', 'dynamic'], { message: 'List type is required' }),
});

export const CreateListForm = (props: CreateListFormProps) => {
	const { submitFormAction } = props;
	const rowsTotal = useSelector(selectRowsTotal);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const filterValues = useSelector(selectFilterValues);
	const searchValue = useSelector(selectSearchValue);

	const filterValuesFromState = {
		...(filterValues as FormValues),
	} as FormObject;

	const initialValues: CreatePatientListRequest = {
		name: '',
		filters: { ...filterValuesFromState },
		search_value: searchValue || undefined,
		list_type: createDynamicListFeatureFlagEnabled ? '' : 'static',
	};

	const form = useAppForm({
		defaultValues: initialValues,
		validators: {
			onMount: schema,
			onChange: schema,
		},
		onSubmit: async ({ value }) => {
			await submitFormAction(value as CreatePatientListRequest);
			trackEvent(`Patient list 'Create list' clicked`);
		},
	});

	const hasFiltersApplied = isFiltersApplied(filterValues as FilterValuesObj);
	const hasSearchApplied = searchValue && searchValue.length > 0;
	const canCreateList = !(
		!rowsTotal ||
		rowsTotal === 0 ||
		!(hasFiltersApplied || hasSearchApplied)
	);

	useEffect(() => {
		if (!canCreateList) {
			// filters have expired, so add notificaton and redirect
			dispatch(
				addNotification({
					type: 'error',
					title: 'Session expired',
					message: 'Your filters have expired, please try again.',
					timeoutLength: 8000,
				})
			);
			navigate('../../', { relative: 'path' });
		}
	}, [canCreateList, dispatch, navigate]);

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				void form.handleSubmit();
			}}
			data-test-id='create-list-form'
		>
			<>
				<div className='grid lg:grid-cols-3 gap-6 mb-8'>
					<Card variant='layoutCard' className='lg:col-span-2 min-h-[calc(100vh-340px)]'>
						<div className='flex flex-col items-center py-16'>
							<img className='w-[124px] mb-5' alt='list icon' src={CreateListSvg} />
							<Typography variant='heading_3_medium' className='mb-1'>
								Save this filter as a patient list
							</Typography>
							<Typography
								variant='body_lg_regular'
								className='mb-8 text-tertiaryTextDark'
							>
								Add a name and choose whether this list should stay fixed or update
								over time.
							</Typography>
							<div className='text-left w-2/3 flex flex-col gap-8'>
								<div data-test-id='create-list-form-name-field'>
									<form.AppField name='name'>
										{(field) => (
											<field.TextField
												type='text'
												label='List name'
												required={true}
											/>
										)}
									</form.AppField>
								</div>
								<div data-test-id='create-list-form-list-type-field'>
									<form.AppField name='list_type'>
										{(field) => (
											<field.RadioField
												groupLabel='List type'
												required={true}
												options={[
													{
														label: 'Static list',
														value: 'static',
														description:
															'Save the current matching patients as a fixed list.',
													},
													{
														label: 'Dynamic list',
														value: 'dynamic',
														description:
															'New patients will be automatically added when they match the saved filter criteria.',
													},
												]}
												variant='card'
												disabled={!createDynamicListFeatureFlagEnabled}
											/>
										)}
									</form.AppField>
								</div>
							</div>
						</div>
					</Card>
					<Card
						variant='layoutCard'
						className='min-h-[calc(100vh-340px)] overflow-hidden'
					>
						<Typography variant='title2' className='mb-6'>
							<AccountOutline className='mr-2' aria-hidden='true' />
							<span className='text-primaryBrand'>{rowsTotal}</span> patient
							{rowsTotal && rowsTotal > 1 ? 's' : ''} will be added to the list
						</Typography>
						<Typography variant='body_lg_medium' className='mb-4'>
							Patients match these criteria:
						</Typography>
						<div className='overflow-y-auto h-full'>
							<SummaryFilter searchValue={searchValue} isUpdatable={false} />
						</div>
					</Card>
				</div>
				<div className='pt-2 mb-8'>
					<div className='flex flex-row justify-between'>
						<Link to='../'>
							<Button type='button' variant='tertiary'>
								Cancel
							</Button>
						</Link>
						<div className='flex flex-row'>
							<form.AppForm>
								<form.SubmitButton>Create</form.SubmitButton>
							</form.AppForm>
						</div>
					</div>
				</div>
			</>
		</form>
	);
};
